import { useEffect } from "react";
import { Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { mutate } from "swr";
import { FormErrorMessage } from "../../components";
import { ApiSchemaVendorData, VendorConnectionType } from "../../models";
import { useFunctionalityVendors } from "../../pages/hooks";
import { isValidUSPhoneNumber, normalizeUSPhoneNumberToE164 } from "../../utils/phoneNumberFormatting";

type Props = {
  show: boolean;
  handleShow: (value: boolean) => void;
  selectedVendor: ApiSchemaVendorData;
  parentCompanyId: string;
  pagedVendorUrl: string;
};

type FormValues = {
  vendorOverflowValue: string;
};

export function ModalEditVendorOverflowValue(props: Props): JSX.Element {
  const { handleShow, selectedVendor } = props;

  const { onEditVendorOverflowValue } = useFunctionalityVendors({
    parentCompanyId: props.parentCompanyId ?? "",
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      vendorOverflowValue: "",
    },
  });

  const isValidSipAddress = (value: string) => {
    return /^sip:[a-zA-Z0-9._%+\-]+@((\d{1,3}\.){3}\d{1,3}|[a-zA-Z0-9\-]+(\.[a-zA-Z0-9\-]+)+)$/i.test(
      value
    );
  };

  useEffect(() => {
    if (selectedVendor) {
      reset({
        vendorOverflowValue: selectedVendor.vendorOverflowValue ?? "",
      });
    }
  }, [selectedVendor, reset]);

  const validateOverflowValue = (value: string) => {
    const trimmed = (value ?? "").trim();
    if (!trimmed) {
      return "Overflow value is required";
    }

    const type = selectedVendor.vendorOverflowType;

    if (type === VendorConnectionType.PhoneNumber) {
      if (!isValidUSPhoneNumber(trimmed)) {
        return "Please enter a valid US phone number (10 digits, optional leading 1. Formatting like (313) 481-4998 is allowed).";
      }
    } else if (type === VendorConnectionType.SipUrl) {
      if (!isValidSipAddress(trimmed)) {
        return "Please enter a valid SIP address (e.g. sip:user@example.com or sip:user@10.0.0.1).";
      }
    }

    return true;
  };

  const editOverflowValueHandler = (form: FormValues) => {
    const trimmed = form.vendorOverflowValue.trim();
    const overflowType = selectedVendor.vendorOverflowType;

    let valueToSave = trimmed;

    if (overflowType === VendorConnectionType.PhoneNumber) {
      const normalized = normalizeUSPhoneNumberToE164(trimmed);
      valueToSave = normalized ?? trimmed;
    }

    onEditVendorOverflowValue(
      { vendorOverflowValue: valueToSave } as any,
      selectedVendor?.id
    )
      .then(() => {
        handleShow(false);
        mutate(props.pagedVendorUrl);
      })
      .catch((e) => console.error(e));
  };


  const overflowType = selectedVendor.vendorOverflowType;

  return (
    <Modal
      show={props.show}
      onHide={() => handleShow(false)}
      centered
      id="editVendorOverflowValueModal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Edit Overflow Value</Modal.Title>
      </Modal.Header>

      <form onSubmit={handleSubmit(editOverflowValueHandler)}>
        <Modal.Body>
          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <label
                  htmlFor=""
                  className="position-relative required"
                >
                  Overflow Value
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={
                    overflowType === VendorConnectionType.PhoneNumber
                      ? "Phone number (e.g. 1-313-481-4998)"
                      : overflowType === VendorConnectionType.SipUrl
                        ? "SIP address (e.g. sip:user@example.com)"
                        : ""
                  }
                  {...register("vendorOverflowValue", {
                    validate: validateOverflowValue,
                  })}
                />
                {errors.vendorOverflowValue && (
                  <FormErrorMessage
                    message={
                      (errors.vendorOverflowValue.message as string) ||
                      "Overflow value is required"
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="btn btn-link text-uppecase text-site"
            onClick={() => handleShow(false)}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary text-uppercase editOverflowValue-from-submit-btn"
          >
            Save
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
